/**
 * NetworkSelector Store - Zustand store with individual selectors
 * Manages network tenant list, host domain, and loading state
 */

import { create } from 'zustand';
import type { NetworkTenantItem } from '@/application/queries/networkTenants';

// ============================================================================
// Store State
// ============================================================================

interface NetworkSelectorState {
  // Data
  tenants: NetworkTenantItem[];
  hostDomain: string;
  loading: boolean;
  error: Error | null;

  // Actions
  setData: (tenants: NetworkTenantItem[], hostDomain: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  _reset: () => void;
}

const EMPTY_TENANTS: NetworkTenantItem[] = [];

const initialState = {
  tenants: EMPTY_TENANTS,
  hostDomain: 'ecgrid.com',
  loading: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

const useNetworkSelectorStore = create<NetworkSelectorState>((set) => ({
  ...initialState,

  setData: (tenants, hostDomain) =>
    set({ tenants, hostDomain, loading: false, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  _reset: () => set(initialState),
}));

// ============================================================================
// Individual Selector Hooks
// ============================================================================

// Data selectors
export const useNetworkTenants = () => useNetworkSelectorStore((s) => s.tenants);
export const useHostDomain = () => useNetworkSelectorStore((s) => s.hostDomain);

// State selectors
export const useNetworkSelectorLoading = () => useNetworkSelectorStore((s) => s.loading);
export const useNetworkSelectorError = () => useNetworkSelectorStore((s) => s.error);

// Action selectors
export const useSetNetworkSelectorData = () => useNetworkSelectorStore((s) => s.setData);
export const useSetNetworkSelectorLoading = () => useNetworkSelectorStore((s) => s.setLoading);
export const useSetNetworkSelectorError = () => useNetworkSelectorStore((s) => s.setError);

// For testing only
export const _resetNetworkSelectorStore = () => useNetworkSelectorStore.getState()._reset();
